/**
 * A cut the dashboard cannot show (Sep 19 2026, review round 47 item 6).
 *
 * A cohort or a slice can come back without figures for three reasons that
 * have nothing to do with the reporting floor: the Worker HELD it back so no
 * student can be singled out (lib/cut-guard.ts, 503 CUT_WITHHELD), a slice is
 * still being PREPARED on an institution that already has figures, or the
 * read FAILED. Each fell through to CohortBuilding until now, which is the
 * first-day screen: "P3 has not computed this cohort's figures yet", "What
 * turns on when you cross 10", and on a slice "Open Invitations", on an
 * institution with a year of figures. The Worker's actual reason sat in the
 * filter note. This card carries that reason, and the way back to figures.
 *
 * The Worker's sentences are shown as they come. A held one opens with the
 * reason ("Held back so no student can be singled out: ..."), which becomes
 * the state's title here and the rest its body; any other wording is shown
 * whole, so a change on the Worker never loses words on the page.
 */

import {
  Button,
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  StateCard,
  buttonClass,
} from "~/app/components/primitives";

export type RefusedCut = {
  /** The cohort's name, or the slice's label ("Matched with a mentor"). */
  readonly name: string;
  readonly kind: "cohort" | "slice";
  readonly state: "held" | "pending" | "down";
  /** The Worker's sentence. */
  readonly reason: string;
};

const HELD_LEAD = /^(Held back so no student can be singled out):\s+(.+)$/s;

/** A held reason's lead as a title and the rest as the body; anything else whole. */
export function splitReason(reason: string): { title: string | null; body: string } {
  const match = HELD_LEAD.exec(reason.trim());
  if (!match) return { title: null, body: reason };
  const rest = match[2]!;
  return { title: match[1]!, body: rest.charAt(0).toUpperCase() + rest.slice(1) };
}

/** The hero's line under the institution's name while a cut is refused. */
export function refusedHeroLine(cut: RefusedCut): string {
  // Short on purpose: the card below says why, in the Worker's words.
  return cut.state === "held"
    ? `${cut.name}: figures held back for now.`
    : cut.state === "pending"
      ? `${cut.name}: figures are being prepared.`
      : `${cut.name}: figures could not be loaded.`;
}

/** Why a dashboard section is not on the page, for the rail. */
export function refusedRailReason(cut: RefusedCut, label: string): string {
  const what = cut.kind === "cohort" ? "this cohort's" : "this group's";
  const why = cut.state === "held" ? "are held back" : cut.state === "pending" ? "are being prepared" : "could not be loaded";
  return `${label} is not shown while ${what} figures ${why}.`;
}

export function CutUnavailable({
  cut,
  legalName,
  onShowAll,
  enrollmentHref,
}: {
  cut: RefusedCut;
  legalName: string;
  /** Back to the whole institution: the cut cleared. */
  onShowAll: () => void;
  /** Enrollment, where a held cohort's way out is (the Worker's own advice:
   *  "Place them in a cohort"). Offered for a held cohort only. */
  enrollmentHref?: string;
}) {
  const noun = cut.kind === "cohort" ? "cohort" : "group";
  const { title, body } = splitReason(cut.reason);
  const heading =
    cut.state === "held"
      ? `Figures for this ${noun} are held back`
      : cut.state === "pending"
        ? `Figures for this ${noun} are being prepared`
        : `Figures for this ${noun} could not be loaded`;
  const stateTitle =
    title ?? (cut.state === "held" ? "Held back" : cut.state === "pending" ? "Not computed yet" : "Not loaded");
  const showAll = (
    <Button variant={cut.state === "held" && enrollmentHref ? "secondary" : "primary"} size="sm" onClick={onShowAll}>
      Show the whole institution
    </Button>
  );

  return (
    <section className="e-sec e-print-page" style={{ marginTop: 0 }} aria-labelledby="cut-unavailable">
      <Card span="full">
        <CardHeader>
          <CardTitle id="cut-unavailable" sub={`${cut.name}, ${legalName}`}>
            {heading}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <StateCard
            variant={cut.state === "down" ? "down" : cut.state === "pending" ? "building" : "empty"}
            title={stateTitle}
            action={
              cut.state === "held" && enrollmentHref ? (
                <span className="e-cut-actions">
                  <a className={buttonClass("primary", "sm")} href={enrollmentHref}>
                    Place students on Enrollment
                  </a>
                  {showAll}
                </span>
              ) : (
                showAll
              )
            }
          >
            {body}
          </StateCard>
        </CardBody>
        <CardFootnote>
          <p className="e-note">
            {cut.state === "down"
              ? "The whole institution's figures are unaffected."
              : `Nothing changes for the students in this ${noun}, and the whole institution's figures are unaffected.`}
          </p>
        </CardFootnote>
      </Card>
    </section>
  );
}
